import AsyncAutocompleteInput, {
  type AsyncAutocompleteInputProps,
} from '@/components/form/AsyncAutocompleteInput'
import TextInput from '@/components/form/TextInput'
import { Typography } from '@/components/ui/typography'
import {
  usePlacesAutocomplete,
  type PlaceDetails,
  type PlaceSuggestion,
} from '@/hooks/use-places-autocomplete'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { truncateFieldValueToCharacterLimit } from '@/lib/field-character-limits'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, type ReactElement, type ReactNode } from 'react'
import { toast } from 'sonner'
import FieldInfo from './FieldInfo'

type AddressSearchOwnProps = {
  id?: string
  label?: ReactNode
  placeholder?: string
  className?: string
  onAddressSelected?: (place: PlaceDetails) => void
  onChange?: (value: string) => void
  allowAutofill?: boolean
  characterMaxLength?: number | null
}

export type AddressSearchProps = AddressSearchOwnProps &
  Omit<
    AsyncAutocompleteInputProps<PlaceSuggestion>,
    | keyof AddressSearchOwnProps
    | 'value'
    | 'fetchOptions'
    | 'onOptionSelected'
    | 'minChars'
    | 'onFetchError'
  >

export default function AddressSearch({
  id,
  label = 'Address',
  placeholder = 'Start typing an address...',
  className = '',
  onAddressSelected,
  onChange,
  allowAutofill = false,
  characterMaxLength = null,
  ...props
}: AddressSearchProps): ReactElement {
  const field = useFieldContext<string | null | undefined>()
  const { fetchSuggestions, getPlaceDetails, resetSession, error } = usePlacesAutocomplete()

  const fetchOptions = useCallback((input: string) => fetchSuggestions(input), [fetchSuggestions])

  const handleOptionSelected = async (option: PlaceSuggestion) => {
    try {
      const addressComponents = await getPlaceDetails(option.value)
      // Keep this field as a plain string. The parent form owns how to map the selected
      // place details into structured address fields (line1/city/state/etc).
      field.handleChange(truncateFieldValueToCharacterLimit(option?.name || '', characterMaxLength))
      onAddressSelected?.(addressComponents)
    } catch (error) {
      console.error('Error getting address details:', error)
    }
  }

  const onFetchError = useCallback(() => {
    toast.error('Error loading address search', {
      description: 'Please enter the address manually or try again later.',
      duration: 5000,
    })
  }, [])

  useEffect(() => {
    if (error) {
      toast.error('Error loading address search', {
        description: (
          <div className="flex flex-col gap-y-2">
            <span>Please enter the address manually or try again later.</span>
            <Typography variant="supporting">Error: {error.message}</Typography>
          </div>
        ),
        duration: 5000,
      })
    }
  }, [error])

  const { isReadOnly } = useIsReadOnly()

  if (isReadOnly) {
    return <TextInput id={id || field.name} label={label} />
  }

  const stringValue = typeof field.state.value === 'string' ? field.state.value : ''

  return (
    <div className={cn('flex w-full flex-col gap-y-2', className)}>
      <AsyncAutocompleteInput
        id={id || field.name}
        label={label}
        placeholder={error ? 'Street Address' : placeholder}
        value={stringValue}
        onChange={(value) => {
          const nextValue = truncateFieldValueToCharacterLimit(value, characterMaxLength)

          field.handleChange(nextValue)
          if (nextValue.length < 3) {
            resetSession?.()
          }
          onChange?.(nextValue)
        }}
        fetchOptions={fetchOptions}
        onOptionSelected={handleOptionSelected}
        allowAutofill={allowAutofill}
        minChars={3}
        onFetchError={onFetchError}
        {...props}
      />
      <FieldInfo />
    </div>
  )
}
